import { getCustomInfrastructureContexts } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/CustomInfrastructureContexts";
import { DurationPicker } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/DurationPicker";
import { getInitialTimedDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/initialDurationValues";
import { InfrastructureForm } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { Duration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import {
  Box,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "shared/components/Spor";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";

type DurationType = Duration["type"];

export const TimedOrOpenEndedDuration = () => {
  const { t } = useTranslation();
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<InfrastructureForm>();
  const countryCode = useCountryCodeForDropsRole();

  const selectedDurationType = watch("infrastructureForm.durations.type");
  const extensionTime = watch(
    "infrastructureForm.durations.extension_time_in_minutes",
  );

  useEffect(() => {
    setValue(
      "infrastructureForm.distributions.contexts",
      getCustomInfrastructureContexts(countryCode),
    );
  }, []);

  const handleTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newDurationType = event.target.value;
      if (newDurationType === "TIMED") {
        setValue(
          "infrastructureForm.durations.to_time",
          getInitialTimedDuration().to_time,
        );
      }
      if (newDurationType === "OPEN_ENDED" && !extensionTime) {
        // Autofill the extension time in minutes field to the normal 150 minutes.
        setValue(
          "infrastructureForm.durations.extension_time_in_minutes",
          "150",
        );
      }
      setValue(
        "infrastructureForm.durations.type",
        event.target.value as DurationType,
      );
    },
    [setValue, extensionTime],
  );

  return (
    <>
      <Box>
        <Text variant="xs" mb="1">
          {t("Starttidspunkt")}
        </Text>

        <DurationPicker
          durationsField="infrastructureForm.durations"
          subField="from_time"
        />
        <RenderErrorInPath
          errors={errors}
          errorPath="infrastructureForm.durations.root"
        />
      </Box>

      <Stack mb="-1">
        <Text variant="xs">{t("Sluttidspunkt")}</Text>
        <RadioGroup
          name={t("Sluttidspunkt")}
          value={selectedDurationType}
          onChange={handleTypeChange}
        >
          <Radio value="OPEN_ENDED">{t("Tidshorisont")}</Radio>
          <Radio value="TIMED">{t("Spesifikt tidspunkt")}</Radio>
        </RadioGroup>
      </Stack>

      {selectedDurationType === "OPEN_ENDED" && (
        <>
          <Input
            value={extensionTime}
            {...register(
              "infrastructureForm.durations.extension_time_in_minutes",
            )}
            label={t("Tidshorisont i min.")}
          />

          <RenderErrorInPath
            errors={errors}
            errorPath="infrastructureForm.durations.extension_time_in_minutes"
          />
        </>
      )}

      {selectedDurationType === "TIMED" && (
        <>
          <DurationPicker
            durationsField="infrastructureForm.durations"
            subField="to_time"
          />
          <RenderErrorInPath
            errors={errors}
            errorPath="infrastructureForm.durations.to_time"
          />
        </>
      )}
    </>
  );
};
